import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Accordion } from "react-bootstrap";
import { updateAuthUser } from "../../store/actions/Auth/AuthActions";
import { clearTourCompletion } from "../../services/Auth/AuthService";
import { PERMISSION_SLUGS, ROLE_SLUGS, userNeedsTransactionPin } from "../../utils/auth/permissionConstants";
import { ADMIN_DETAILS_SUPER_ADMIN_SETUP_TOUR_ID } from "./PartnerDetails.tour";
import { ADMIN_DASHBOARD_SUPER_ADMIN_INTRO_TOUR_ID } from "./Vault/AdminDashboard.tour";
import { MY_ACTIVITY_TOUR_ID } from "./MyActivity.tour";
import { MY_WALLET_DASHBOARD_TOUR_ID } from "./Vault/MyWalletDashboard.tour";
import { USERS_OVERVIEW_TOUR_ID, USERS_ADD_USER_TOUR_ID } from "./Users.tour";
import { REVERSALS_PAGE_OVERVIEW_TOUR_ID } from "./ReversalsPage.tour";
import { MY_REQUESTS_OVERVIEW_TOUR_ID } from "./PartnerReceipts/MyRequests.tour";
import { APPROVALS_OVERVIEW_TOUR_ID } from "./PartnerReceipts/Approvals.tour";
import { MY_RECEIPTS_OVERVIEW_TOUR_ID } from "./MyReceiptsMainTable.tour";
import { MANAGE_RECEIPTS_OVERVIEW_TOUR_ID } from "./ReceiptReviewsMainTable.tour";
import { FLOAT_HEALTH_TOUR_ID } from "./FloatHealth.tour";
import { MANAGE_PARTNERS_OVERVIEW_TOUR_ID } from "./ManagePartners.tour";
import useGuidedTour from "../../hooks/useGuidedTour";
import {
  MY_SECURITY_WALKTHROUGH_TOUR_ID,
  buildSecurityWalkthroughSteps,
} from "../../hooks/faqOutroSteps";
import { usePartnerHasSoloUser } from "../../hooks/usePartnerHasSoloUser";

const WALKTHROUGHS = [
  {
    tourId: MY_SECURITY_WALKTHROUGH_TOUR_ID,
    title: "My Security",
    description: "Walk through Sign-in activity, Permissions, Change PIN, and Forgot PIN, and end at your transaction PIN.",
    route: "/faq?tour=security",
  },
  {
    tourId: MY_WALLET_DASHBOARD_TOUR_ID,
    title: "Wallet dashboard",
    description: "See how your balance, top ups, and payments come together on the wallet dashboard.",
    route: "/my-balance-dashboard",
    requiresUserWallet: true,
  },
  {
    tourId: MY_ACTIVITY_TOUR_ID,
    title: "Mili History",
    description: "Learn how the Statement, Disbursements, and Payouts tabs each show a different slice of your activity.",
    route: "/my-activity",
    requiresUserWallet: true,
  },
  {
    tourId: ADMIN_DASHBOARD_SUPER_ADMIN_INTRO_TOUR_ID,
    title: "Vault dashboard",
    description: "Get oriented on the vault dashboard, its balances, and how to top up team wallets from it.",
    route: "/vault-dashboard",
    requiredPermissionSlug: PERMISSION_SLUGS.VIEW_PARTNER_DASHBOARD,
    requiresPartnerVault: true,
    requiresNotSoloPartner: true,
  },
  {
    tourId: ADMIN_DETAILS_SUPER_ADMIN_SETUP_TOUR_ID,
    title: "Organization",
    description: "Walk through the setup order for expense tags, teams, and users on your organization.",
    route: "/admin-details",
    requiredPermissionSlug: PERMISSION_SLUGS.VIEW_PARTNER_DETAILS,
    requiresPartner: true,
  },
  {
    tourId: USERS_OVERVIEW_TOUR_ID,
    title: "Users page overview",
    description: "Tour the search, filters, row actions, and pagination on the standalone Users page.",
    route: "/users",
    requiredPermissionSlug: PERMISSION_SLUGS.VIEW_PARTNER_DETAILS,
    requiresPartner: true,
  },
  {
    tourId: USERS_ADD_USER_TOUR_ID,
    title: "Add a user",
    description: "Walk through the Add User form field by field, including the wallet-and-team branch.",
    route: "/users?tour=add-user",
    requiredPermissionSlug: PERMISSION_SLUGS.MANAGE_PARTNER_USERS,
    requiresPartner: true,
  },
  {
    tourId: MY_REQUESTS_OVERVIEW_TOUR_ID,
    title: "Mili Requests",
    description: "See how to raise a limit extension or wallet top up, track its status, and cancel a pending one.",
    route: "/my-requests",
    requiresUserWallet: true,
    requiresPartner: true,
  },
  {
    tourId: APPROVALS_OVERVIEW_TOUR_ID,
    title: "Approvals",
    description: "Learn how to approve or reject a request, and what the held for top up outcome means.",
    route: "/approvals",
    requiredPermissionSlug: PERMISSION_SLUGS.CAN_REVIEW_APPROVALS,
    requiresPartner: true,
  },
  {
    tourId: MY_RECEIPTS_OVERVIEW_TOUR_ID,
    title: "My Receipts",
    description: "See how to attach a receipt to a payment, find an old one, and what the row actions do.",
    route: "/my-receipts",
    requiresUserWallet: true,
    requiresPartner: true,
  },
  {
    tourId: MANAGE_RECEIPTS_OVERVIEW_TOUR_ID,
    title: "Manage Receipts",
    description: "Walk through the review flow: filter the list, open a receipt, decide, and the accountability rule that stops you approving your own uploads.",
    route: "/manage-receipts",
    requiredPermissionSlug: PERMISSION_SLUGS.MANAGE_RECEIPTS,
    requiresPartner: true,
  },
  {
    tourId: MANAGE_PARTNERS_OVERVIEW_TOUR_ID,
    title: "Onboard a partner",
    description: "Walk through registering a partner organization and inviting the super admin who will run it.",
    route: "/manage-partners",
    requiredRoleSlug: ROLE_SLUGS.EXEC_ADMIN,
  },
  {
    tourId: FLOAT_HEALTH_TOUR_ID,
    title: "Read the float dashboard",
    description: "Walk the six cards on the Float Health screen and learn when to rebalance.",
    route: "/health",
    requiredRoleSlug: ROLE_SLUGS.EXEC_ADMIN,
  },
  {
    tourId: REVERSALS_PAGE_OVERVIEW_TOUR_ID,
    title: "Understand the two kinds of reversals",
    description: "Walk through the Reversals page and learn when to run an automatic reversal versus when to record one the vendor already sent back.",
    route: "/reversals",
    requiredRoleSlug: ROLE_SLUGS.EXEC_ADMIN,
  },
];

const FAQ_ENTRIES = [
  {
    id: "sign-in-activity-this-wasnt-me",
    question: "I saw a sign in I do not recognize. What do \"This wasn't me\" and Sign out do?",
    answer:
        "This wasn't me flags that session as suspicious for our security team and prompts you to change your password right away. Sign out ends that session on that device but does not raise a flag. Use This wasn't me any time the location, device, or timing looks off.",
  },
  {
    id: "walkthroughs-what",
    question: "What are the guided tours?",
    answer:
      "Short pop ups that point out the key parts of a screen the first time you open it. They run once, then get out of your way. You can replay any of them from the list at the top of this page.",
  },
  {
    id: "walkthroughs-replay",
    question: "How do I replay a tour?",
    answer:
      "Pick the one you want from the tours list above and click Take this tour. You will land on the right screen and the tour will start on its own.",
  },
  {
    id: "payout-under-review-then-email",
    question: "I was told my payment was under review, then I got an email later saying it completed or was returned. What happened?",
    answer:
      "M-PESA sometimes takes longer than usual to confirm a payment back to us, so we mark it as under review and hold your money in place. A few minutes later we check with M-PESA directly, and once we hear back we email you the outcome. If the payment went through, the transaction is already on your account. If it did not, the amount is back in your wallet and you can retry when you are ready.",
    requiresUserWallet: true,
  },
  {
    id: "find-mpesa-receipt-code",
    question: "Where do I find the M-PESA receipt code for a payment I made?",
    answer:
      "Open My Payouts (or the latest transactions block on your wallet dashboard), find the row for the payment, and either tap anywhere on the row or the small eye badge next to the recipient. A card opens with the recipient's name, the number you paid (marked with a Paybill or Till chip when you paid a business, and with an Account chip on the account number for a paybill), the amount, and the receipt code in a larger font so you can read it aloud at a counter. The card stays open for a full minute before it hides itself, so you have time to write it onto a paper receipt without it disappearing on you.",
    requiresUserWallet: true,
  },
  {
    id: "see-topup-details-from-vault-dashboard",
    question: "How do I see the full details of a top-up on the vault dashboard?",
    answer:
      "On the Latest Top-Ups block on the vault dashboard, either tap anywhere on the row or the small eye badge inside it. A card opens with the amount, the phone number the money came from, the M-PESA receipt code, the current status, and when it was received. The receipt sits in a larger font so you can read it back to whoever paid in.",
    requiredPermissionSlug: PERMISSION_SLUGS.VIEW_PARTNER_DASHBOARD,
    requiresNotSoloPartner: true,
  },
  {
    id: "see-capital-injection-details",
    question: "How do I see the full details of a capital injection?",
    answer:
      "On the Capital Injections page, either tap anywhere on the row or the small eye badge in it. A card opens with the amount, who recorded it, the description you or your colleague typed, and when it was recorded. If the person who recorded it has since been removed, their name still shows with a small (deleted) tag so the trail stays intact.",
    requiredRoleSlug: ROLE_SLUGS.EXEC_ADMIN,
  },
  {
    id: "share-receipt-from-details-card",
    question: "How do I share a receipt with someone?",
    answer:
      "Open the details card for a transaction or top-up (tap the row or the small eye badge on it) and tap Share receipt at the bottom of the card. On a phone your usual share sheet opens so you can send the receipt over WhatsApp, SMS, email, or any other app you have installed. On a desktop the receipt is copied to your clipboard instead, so you can paste it wherever you need it. Either way the receipt carries the amount, the recipient or source, the M-PESA receipt code, the status, and when it happened.",
  },
  {
    id: "change-transaction-pin",
    question: "How do I change my transaction PIN?",
    answer:
      "Open the sidebar, expand My Security, and choose Change PIN. If you have forgotten your PIN, pick Forgot PIN in the same menu to reset it. Forgot PIN asks for your account password before it emails you a one-time code, so nobody can trigger a code and race for it without first knowing your password.",
    requiresTransactionPinCapability: true,
  },
  {
    id: "approval-held-for-top-up",
    question: "I approved a request. Why is the requester still waiting?",
    answer:
      "If the wallet the money needs to leave from does not hold enough right now, the approval is parked until a top up covers it. Nothing has failed. The request stays visible under Mili Requests for the requester, and the disbursement runs automatically once the wallet is funded.",
    requiredAnyPermissionSlug: [
      PERMISSION_SLUGS.REVIEW_WALLET_DISBURSEMENT_REQUESTS,
      PERMISSION_SLUGS.REVIEW_LIMIT_EXTENSION_REQUESTS,
    ],
  },
  {
    id: "pin-lockout-spans-every-screen",
    question: "I mistyped my PIN a few times on one screen and now nothing else asks for a PIN will accept it. Why?",
    answer:
      "There is one transaction PIN per person and one lockout counter behind it. Every screen that asks for the PIN (paying from your own wallet, topping up a teammate's, or approving a wallet top-up request) counts against the same tally. Five wrong tries in a row locks the PIN for 30 minutes on every one of those screens, not just the one where you typed the wrong PIN. Waiting out the 30 minutes clears the lock, and so does resetting the PIN through Forgot PIN or changing it through Change PIN. A correct PIN also clears the counter, so a single successful use resets you back to a full five tries.",
    requiresTransactionPinCapability: true,
  },
  {
    id: "whose-pin-when-approving-top-up",
    question: "When I approve a wallet top-up request, whose PIN am I entering, mine or the person who asked?",
    answer:
      "Yours. The prompt is a sign-off from you, the reviewer, so a stolen or borrowed session cannot rubber-stamp a top-up in your name. The requester never sees or types your PIN, and nothing about their request depends on knowing it. If you cannot approve because you have not set a PIN yet, set one from My Security in the sidebar and try again; do not ask the requester for theirs.",
    requiredPermissionSlug: PERMISSION_SLUGS.REVIEW_WALLET_DISBURSEMENT_REQUESTS,
  },
  {
    id: "how-do-i-know-i-am-a-team-reviewer",
    question: "How do I know if I am a team reviewer?",
    answer:
      "A small Team reviewer pill sits next to your name on your profile page and on your row in the users list. If you review one or more teams, the profile page also shows the teams by name in a Reviews field. If you should be a reviewer and do not see the pill, ask a super admin on your account to assign you.",
  },
  {
    id: "cannot-see-approvals-queue",
    question: "I am on a team but do not see the Approvals list. Why?",
    answer:
      "Only users with permission to review the request type see it. Reviewing wallet top ups and reviewing limit extensions are separate permissions, and accountants are deliberately kept out of limit extension reviews so the person who initiates a disbursement is not also the person who approved the limit for it. If you should be reviewing requests, ask a super admin on your account to assign you as a team reviewer for the team whose requests you are meant to see.",
    forbiddenAnyPermissionSlug: [
      PERMISSION_SLUGS.REVIEW_WALLET_DISBURSEMENT_REQUESTS,
      PERMISSION_SLUGS.REVIEW_LIMIT_EXTENSION_REQUESTS,
    ],
  },
  {
    id: "new-request-button-disabled",
    question: "My New Request button on Mili Requests is disabled. What is missing?",
    answer:
      "You need at least one transaction limit type on your account before you can ask for a higher limit. An administrator assigns those from your user record. Once one is in place, refresh the page and the button will be available.",
    requiresUserWallet: true,
  },
  {
    id: "new-request-button-disabled-solo-admin",
    question: "I am the only admin on my account and my New Request button on Mili Requests is disabled. What is missing?",
    answer:
      "You need a second admin on the account before you can raise a limit extension request, so that someone else is accountable for approving it. Invite another super admin from Users, or assign an existing user as a team reviewer for your team. Once a second reviewer is in place, refresh the page and the button will be available.",
    requiresUserWallet: true,
    requiredPermissionSlug: PERMISSION_SLUGS.MANAGE_PARTNER_USERS,
  },
  {
    id: "temporary-limit-expiry",
    question: "What happens when a temporary transaction limit expires?",
    answer:
      "Nothing you have to do. Temporary limits stack on top of your primary limit for a fixed window. When the window ends, the temporary limit is removed automatically overnight and your primary limit is what applies again.",
    requiresUserWallet: true,
  },
  {
    id: "cancel-my-pending-request",
    question: "Can I cancel a request I already submitted?",
    answer:
      "Yes, as long as no one has responded to it yet. Open Mili Requests, find the pending request in the list, and use Cancel on that row. Once a reviewer has approved or denied it, cancel is no longer available.",
    requiresUserWallet: true,
  },
  {
    id: "cannot-review-own-receipt",
    question: "Why can't I review my own receipts on Manage Receipts?",
    answer:
      "Milipot protects your audit trail through accountability: whoever submits a receipt and whoever approves it are always two different people. That applies to every role, including super admin. Ask another super admin or an accountant on your account to sign it off. If your account has no other admin who can review it, a super admin should invite a second one from Users so someone can be accountable for reviewing your receipts.",
    requiredPermissionSlug: PERMISSION_SLUGS.MANAGE_RECEIPTS,
  },
  {
    id: "cannot-approve-own-limit-extension",
    question: "Why can't I approve my own limit extension request?",
    answer:
      "Milipot protects your audit trail through accountability: whoever asks for a higher limit and whoever approves it are always two different people. That applies to every role, including super admin. Another super admin, or a team reviewer assigned to your team, has to sign it off. If your account has no other admin who can approve it, a super admin should invite a second one from Users so someone can be accountable for approving your request.",
    requiresUserWallet: true,
  },
  {
    id: "cannot-change-own-transaction-limits",
    question: "Why can't I change my own transaction limits?",
    answer:
      "Milipot protects your audit trail through accountability: nobody adjusts their own transaction limits, regardless of role. Another super admin on your account has to make the change for you. If your account has no other super admin, a second one should be invited from Users so someone can be accountable for setting your limits.",
    requiresUserWallet: true,
  },
  {
    id: "wallet-vs-vault-balance",
    question: "What is the difference between my wallet balance and the vault balance?",
    answer:
      "Your wallet balance is money assigned to you personally and only you can spend from it. The vault balance is your organization's pooled float that administrators top up user wallets from. Regular users only see their own wallet. Administrators see both.",
    requiresUserWalletOrPartnerVault: true,
    requiresNotSoloPartner: true,
  },
  {
    id: "solo-partner-how-to-top-up",
    question: "How do I top up my wallet?",
    answer:
      "Open your balance dashboard and pick the Top up tab. Enter the phone number to bill and the amount, then submit. Milipot sends an M-Pesa STK push to that phone. Once it is approved and M-Pesa confirms, the money lands in your wallet automatically and the receipt shows up in Latest Top-Ups just below.",
    requiresUserWallet: true,
    requiresSoloPartner: true,
  },
  {
    id: "forgot-password-vs-forgot-pin",
    question: "What is the difference between resetting my password and resetting my PIN?",
    answer:
      "Your password lets you sign in to the app. Your PIN authorizes money movements (top ups, sends, payouts). They are separate on purpose, so a stolen password alone cannot move funds. Reset the password from the sign in page and the PIN from My Security in the sidebar.",
    requiresTransactionPinCapability: true,
  },
  {
    id: "resend-partner-super-admin-verification",
    question: "The super admin I invited for a partner hasn't verified yet. Can I resend the pin?",
    answer:
      "Yes. Open Manage Partners, click the row for that partner to open its details, and use Resend verification pin. A fresh 6-digit code goes to the same address on file, and any earlier pin at that address stops working. If the address itself was mistyped, use Fix email instead so the pin reaches the right inbox.",
    requiredRoleSlug: ROLE_SLUGS.EXEC_ADMIN,
  },
  {
    id: "fix-partner-super-admin-email",
    question: "I typed the wrong email for a partner's super admin. Can I fix it before they verify?",
    answer:
      "Yes, as long as they have not yet verified. Open Manage Partners, click the partner row to open its details, and use Fix email. Enter the correct address; the record updates, any pin sent to the old address stops working, and a fresh pin goes to the new one. Once the super admin has verified, this action is no longer available and any further email change happens through the regular edit path.",
    requiredRoleSlug: ROLE_SLUGS.EXEC_ADMIN,
  },
  {
    id: "capital-injection-reconciliation-when-and-what-to-correct",
    question: "Why is a capital injection showing up on the Reconciliation page, and what should I fix?",
    answer:
      "A capital injection lands on that list in one of two situations. Either Safaricom took the money but the callback we needed to record the receipt never arrived, and our follow-up query confirmed the payment succeeded, so the row is waiting for you to enter the receipt, phone number, transaction date, and the actual charged amount. Or the initial push itself had an unknown outcome (a network hiccup or a saved response we lost), so the row is waiting for someone to confirm what happened. In both cases the amount and phone you see on the row are what was initiated, not necessarily what Safaricom charged. Open the Safaricom Business Manager portal, find the transaction, and enter what actually happened; if the amount or phone differs from the initiated value, correct it here so the ledger books the real figure.",
    requiredRoleSlug: ROLE_SLUGS.EXEC_ADMIN,
  },
  {
    id: "mpesa-fee-variance-what-is-it",
    question: "What is the M-PESA Fee Variances tab on the Reconciliation page?",
    answer:
      "It lists payouts where Safaricom charged more for the transaction than the app reserved from the user's wallet, and the wallet was too empty at that moment to cover the shortfall on its own. When the wallet does have enough at settlement, the app charges the difference automatically and no row lands on this list. So a row here means the user still owes the extra fee and the platform is holding it as an expense until you either claw it back or write it off.",
    requiredRoleSlug: ROLE_SLUGS.EXEC_ADMIN,
  },
  {
    id: "mpesa-fee-variance-claw-back-vs-write-off",
    question: "Should I claw back or write off an M-PESA fee variance?",
    answer:
      "Claw back once the user's wallet has been topped up enough to cover the shortfall; that debits the user for the extra fee and reverses the platform's provisional expense entry. Write off when you have confirmed with Safaricom that the extra charge was a glitch on their side, or when the amount is too small to pursue. Write off requires a short reason so the audit trail shows why the platform absorbed the loss. The Claw back button is disabled automatically until the wallet can cover the shortfall.",
    requiredRoleSlug: ROLE_SLUGS.EXEC_ADMIN,
  },
  {
    id: "reversals-admin-vs-vendor-initiated",
    question: "What is the difference between Admin initiated and Vendor initiated reversals?",
    answer:
      "Admin initiated is when you kick off the reversal in the app and our system calls Safaricom to pull the money back on M-Pesa. Vendor initiated is when the recipient has already sent the money back to us themselves over M-Pesa, so Safaricom is done and you are only recording the incoming payment against the original payout so the ledger catches up. If you have to do something on M-Pesa's side, use Admin initiated. If the vendor already did it, use Vendor initiated.",
    requiredRoleSlug: ROLE_SLUGS.EXEC_ADMIN,
  },
  {
    id: "vendor-reversal-how-to-record",
    question: "How do I record a refund the vendor sent back on their own?",
    answer:
      "Open Reversals from the sidebar, switch to the Vendor initiated tab, and click Record refund. Paste the Original Receipt ID of the payout you are refunding; the modal looks it up and shows you who is being refunded, the original amount, how much has been refunded already, and how much is still open. Fill in the Refund ID (the M-Pesa receipt from the money the vendor sent back), the amount that actually came back, when it landed, and a short comment. If you also want to refund the platform fee or the M-Pesa fee, tick the boxes; the modal shows you exactly what each one will add. On the next screen you get a table of exactly what will be booked. Click Record refund to save.",
    requiredRoleSlug: ROLE_SLUGS.EXEC_ADMIN,
  },
  {
    id: "vendor-reversal-partial-refunds",
    question: "Can I record a partial refund, or several refunds against the same payout?",
    answer:
      "Yes. If the vendor sent back only part of what they were paid, enter that amount and record it. Later, if they send more, record a second refund against the same original payout; the modal will show you how much is already refunded and how much is still open. You cannot record more than the payout amount in total; the system refuses the third try if the running total would go over.",
    requiredRoleSlug: ROLE_SLUGS.EXEC_ADMIN,
  },
  {
    id: "vendor-reversal-duplicate-receipt",
    question: "I got an error saying the M-Pesa receipt has already been recorded. What happened?",
    answer:
      "Someone (maybe you a moment ago, maybe a colleague) has already recorded a vendor refund with that same Refund ID. M-Pesa receipts are unique, so the same one can only be booked once. Open the Vendor initiated tab and look for that Refund ID in the list to see the earlier entry. If the original record is correct, nothing else to do; if it was wrong, the record cannot be edited from the UI and needs a developer to correct.",
    requiredRoleSlug: ROLE_SLUGS.EXEC_ADMIN,
  },
  {
    id: "vendor-reversal-fee-checkbox-disabled",
    question: "Why is the Platform fee refund checkbox greyed out for some payouts?",
    answer:
      "The checkbox is only enabled when the original payout captured a fee snapshot at settlement time. Older payouts made before that snapshot was recorded do not carry the numbers needed to refund the fee cleanly, so we hide the option rather than guess. If the payout is old enough to be missing a snapshot, refund the principal only and leave the fee where it is.",
    requiredRoleSlug: ROLE_SLUGS.EXEC_ADMIN,
  },
  {
    id: "total-wallet-balances-card",
    question: "What is the Total Wallet Balances card on the vault dashboard?",
    answer:
      "The running sum of every active user wallet on your organization, computed live from the ledger. Deactivated wallets and unverified users are not counted. Click the card to open the standalone Total Wallet Balances page, which lists every active wallet holder with their current balance and lets you search by name.",
    requiredPermissionSlug: PERMISSION_SLUGS.VIEW_PARTNER_DASHBOARD,
    requiresNotSoloPartner: true,
  },
  {
    id: "delete-user-and-reuse-email",
    question: "What happens when I delete a team member, and can I re-add them later?",
    answer:
      "Deletion removes the account from the active list and cannot be reversed. Any balance in that person's wallet is reversed to the vault first, and they receive an email confirming the reversal. You can invite a fresh account with the same email or phone later. The new account starts empty and inherits nothing from the deleted one. Historical records that already mention the old account continue to show that person's name for accountability.",
    requiredPermissionSlug: PERMISSION_SLUGS.MANAGE_PARTNER_USERS,
  },
  {
    id: "uncategorized-tag-cannot-be-deleted",
    question: "Why can't I delete the Uncategorized tag?",
    answer:
      "Uncategorized is the starter tag Milipot creates for every organization so a wallet can be activated and a team can be added on day one, before you have set up your own categories. Because that safety net has to always be present, the tag cannot be deleted; the delete button does not appear on its row. You can rename it to anything that fits your organization better, and it behaves like any other tag once renamed.",
    requiredPermissionSlug: PERMISSION_SLUGS.MANAGE_EXPENSE_TAGS,
  },
  {
    id: "suggested-expense-tag-chips",
    question: "What are the suggested tag chips above the expense tags table?",
    answer:
      "They are one-click shortcuts for the common categories most organizations end up using: Rent, Lunch, Transport, Airtime, and Stationery. Click a chip and a tag with that exact name is added to your list. A chip disappears the moment a tag with that name exists (case-insensitive), so you only ever see suggestions that still add value. When you have added them all, or when your own tags already cover them, the entire row hides. If the suggestions are not useful for your organization, tap Dismiss on the row and it stays hidden on this device for this organization. You can still use the Add Expense Tag button any time for a custom category.",
    requiredPermissionSlug: PERMISSION_SLUGS.MANAGE_EXPENSE_TAGS,
  },
  {
    id: "add-team-blocked-by-expense-tag",
    question: "Why can't I add a team?",
    answer:
      "A team spends against an expense tag, so at least one tag has to exist before the first team can be created. If your organization has no tags yet, the Add Team button on Organization is disabled and a notice above the list points you to Add expense tag. Add one tag on Organization first, then come back to Teams and the button will be available.",
    requiredPermissionSlug: PERMISSION_SLUGS.MANAGE_TEAMS,
  },
  {
    id: "activate-wallet-blocked-by-expense-tag",
    question: "Why is Activate Wallet failing for a user?",
    answer:
      "Every wallet spends against an expense tag and every wallet holder belongs to a team, so your organization needs at least one expense tag and at least one team before any wallet can be activated. Open the Activate Wallet screen for that user; if a notice says an expense tag or a team is missing, use the shortcut in the notice, add the missing piece on Organization, then come back and Activate Wallet will succeed.",
    requiredPermissionSlug: PERMISSION_SLUGS.MANAGE_PARTNER_USERS,
  },
  {
    id: "self-activate-wallet-blocked-by-expense-tag",
    question: "Why is Activate My Wallet failing on my profile?",
    answer:
      "Your wallet will spend against an expense tag and belong to a team, so your organization needs at least one of each before Activate My Wallet can succeed. Open Activate My Wallet on your profile; if a notice says an expense tag or a team is missing, use the shortcut to jump to Organization, add the missing piece, then come back and finish activating.",
    requiredRoleSlug: ROLE_SLUGS.SUPER_ADMIN,
  },
  {
    id: "assign-team-blocked-by-no-teams",
    question: "Why is the Assign Team picker empty?",
    answer:
      "There are no teams to assign this user to yet. Open the Organization page and add a team, then come back to Assign Team and the picker will show it. If you meant to move the user to a specific team that already exists, refresh; the list is cached for a few minutes.",
    requiredPermissionSlug: PERMISSION_SLUGS.MANAGE_PARTNER_USERS,
  },
  {
    id: "assign-user-to-team",
    question: "How do I move a user to a different team, or add them to one for the first time?",
    answer:
      "Open the Users table on the partner page, tap the three-dot button on the user's row, and choose Assign Team. Pick the team and save. Their wallet stays active and their balance stays put; only the team changes. The option only appears for users who already have a wallet, since a team assignment is meaningless without one. A user who does not yet have a wallet gets their first team through Activate Wallet (for accountants and default users) or through the self-activate step on their own profile page (for super admins).",
    requiredPermissionSlug: PERMISSION_SLUGS.MANAGE_PARTNER_USERS,
  },
  {
    id: "super-admin-activate-own-wallet",
    question: "I am a super admin without a wallet. How can I start spending from Milipot myself?",
    answer:
      "Activate your wallet from your profile page. You do not need a wallet to run the account, since you can top up other people from your vault and let them spend. But if you also want to spend from Milipot yourself, open your profile and use Activate wallet. Once it is active, your own top-ups and payouts work like any other user's.",
    requiredRoleSlug: ROLE_SLUGS.SUPER_ADMIN,
    hiddenWhenUserHasWallet: true,
    requiresNotSoloPartner: true,
  },
  {
    id: "phone-optional-on-user-add",
    question: "Do I have to enter a phone number when I add a user?",
    answer:
      "No. Only first name, last name, email, and role are required. Phone is optional and safe to skip if you do not have it yet. You can add or edit it later from the same user's row without affecting anything else.",
    requiredAnyPermissionSlug: [
      PERMISSION_SLUGS.MANAGE_PARTNER_USERS,
    ],
  },
  {
    id: "delete-team",
    question: "How do I delete a team, and what does that mean for the people on it?",
    answer:
      "Open the Teams card on the partner page and tap the red trash icon on the team's row. A dialog opens and tells you what will change. First it checks two things: the team must have no members left, and it must have no pending approvals awaiting review. If either is present, the dialog explains what to do first (reassign members to another team, or wait for the approvals to be reviewed). If the team is clear, the dialog lists each reviewer of the team and tells you which of them would lose reviewer permissions across the app because this was their only team, and which would keep them because they still review another team. Deleting is permanent for that team, and you can create a fresh team with the same name later without any collision with the old one.",
    requiredPermissionSlug: PERMISSION_SLUGS.MANAGE_TEAMS,
  },
  {
    id: "solo-why-no-teams",
    question: "Why do I not see a Teams section on my Organization page?",
    answer:
      "You are the only person on this account right now, so teams have nowhere to route to yet. We keep the page tidy while there is only one of you. The moment you invite someone else with a wallet, teams appear and you get to name yours or split into a new one. A default team is already sitting behind the scenes, so nothing is missing, it is just quiet.",
    requiresSoloPartner: true,
    requiredPermissionSlug: PERMISSION_SLUGS.VIEW_PARTNER_DETAILS,
  },
  {
    id: "solo-first-teammate-with-wallet",
    question: "What happens when I add my first teammate with a wallet?",
    answer:
      "When you flip the wallet toggle on for the first person who joins you, a small section appears in the Add User form asking which team they should join. You can keep the default team you already have (and rename it inline if you want), or start a new team for them. Whichever you pick, the Teams section then appears across the app so future invites can slot into it or split off further.",
    requiresSoloPartner: true,
    requiredPermissionSlug: PERMISSION_SLUGS.MANAGE_PARTNER_USERS,
  },
  {
    id: "own-limit-after-peer-joins",
    question: "How do I change my own spending limit now that another admin is on the account?",
    answer:
      "Limit changes now go through peer review for accountability, so you cannot set your own limit directly any more. From the Approvals page, submit an extension request for the amount you need. Any other super admin on the account can approve it. Your primary limit does not change; the extension sits alongside it and expires on its own.",
    requiresNotSoloPartner: true,
    requiredPermissionSlug: PERMISSION_SLUGS.MANAGE_USER_TRANSACTION_LIMITS,
    requiresUserWallet: true,
  },
  {
    id: "float-health-what-is-runway",
    question: "What does the runway clock on the Float Health screen mean?",
    answer:
      "Runway is a rough estimate of how long each M-Pesa float account has before it runs dry, based on how fast money has been flowing out of it in the last hour. Green means comfortable, amber means keep an eye on it, red means top up soon. It is a signal, not a promise: a single big payment can shorten it quickly.",
    requiredRoleSlug: ROLE_SLUGS.EXEC_ADMIN,
  },
  {
    id: "float-health-when-to-rebalance",
    question: "When should I rebalance the float?",
    answer:
      "When the rebalance card on Float Health shows a specific amount and direction, that side of the float is running short while the other has room. Copy the amount, log in to the Safaricom Business Portal, complete the move between the utility and working accounts, then log the transfer under Fund Transfers so the ledger stays in sync.",
    requiredRoleSlug: ROLE_SLUGS.EXEC_ADMIN,
  },
  {
    id: "float-health-ledger-vs-safaricom",
    question: "Why does the ledger balance on Float Health sometimes differ from what Safaricom shows?",
    answer:
      "Our internal ledger updates the moment we know about a payment. Safaricom's own balance updates on their side, and the numbers we compare against are refreshed every fifteen minutes. Small differences during that window are normal and usually resolve on the next refresh. A sustained mismatch triggers a reconciliation alert to the dev team, so investigate the flagged account rather than every tiny wobble.",
    requiredRoleSlug: ROLE_SLUGS.EXEC_ADMIN,
  },
  {
    id: "float-health-blocked-payouts",
    question: "What are the blocked payouts on the Float Health screen?",
    answer:
      "When a user tries to pay something but our M-Pesa float on the right side is too low to cover it, we refund the user's wallet, ask them to try again in a few minutes, and log the attempt as a blocked payout. A spike here is a live signal that float is running out. Once you rebalance, users retry successfully.",
    requiredRoleSlug: ROLE_SLUGS.EXEC_ADMIN,
  },
  {
    id: "float-health-sync-with-daraja",
    question: "What does the Sync with Daraja button next to the Daraja balance do?",
    answer:
      "It asks Daraja for the current balance on that account right now, so you do not have to wait for the next scheduled sync. While the sync is running, the Daraja balance and the last synced time show a spinner so you can see exactly which values will change. Daraja usually replies within ten to fifteen seconds, and the dashboard auto-refreshes so the fresh number appears. To keep the shortcode from being spammed, this button is rate limited to one call per minute across all admins.",
    requiredRoleSlug: ROLE_SLUGS.EXEC_ADMIN,
  },
  {
    id: "float-health-refresh-now",
    question: "What does the Refresh now button at the top of the Float Health screen do?",
    answer:
      "It rebuilds the numbers on the dashboard on the spot, so a top-up or payout you just made appears without waiting for the next automatic refresh. While the refresh runs, every value that could change shows a small spinner in place of the number, so you can see clearly what is being updated. It does not talk to Daraja (use Sync with Daraja for that). It is rate limited to a handful of clicks per minute to keep the servers healthy.",
    requiredRoleSlug: ROLE_SLUGS.EXEC_ADMIN,
  },
  {
    id: "how-do-i-change-a-user-role",
    question: "How do I change a user's role?",
    answer:
      "Open the Users page, find the person you want to reassign, and open the row action menu. Pick Change Role. A window opens with a dropdown of the roles you can move them to, an optional reason field, and a Current password field at the bottom. Type your own current password to confirm, then click Update. You cannot change your own role from here, and a person who has not yet verified their invite email is not eligible until they do.",
    requiredPermissionSlug: PERMISSION_SLUGS.CHANGE_USER_ROLE,
  },
  {
    id: "role-change-clears-special-permissions",
    question: "What happens to the person's special permissions when I change their role?",
    answer:
      "They are cleared as part of the change. Special permissions are grants layered on top of a role, so once the role moves, those grants are wiped and the person only holds whatever the new role provides on its own. If a specific permission should still apply after the change, grant it again from their profile once the reassignment is done. The change is recorded, including exactly which permissions were cleared, so you can review what was revoked if you need to.",
    requiredPermissionSlug: PERMISSION_SLUGS.CHANGE_USER_ROLE,
  },
  {
    id: "role-change-signs-user-out",
    question: "Why does the person I just reassigned need to sign in again?",
    answer:
      "Every active session on their account is signed out the moment the role changes. This is deliberate: their permissions are different now, and letting an existing session keep acting under the old role for even a short while would be a security gap. They can sign back in immediately with the same email and password; on the next sign in, the app reflects the new role.",
    requiredPermissionSlug: PERMISSION_SLUGS.CHANGE_USER_ROLE,
  },
];

const FAQ_PAGE_SIZE = 5;
const WALKTHROUGHS_PAGE_SIZE = 5;

const Faq = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authUser = useSelector((state) => state.auth.authUser);
  const [searchParams, setSearchParams] = useSearchParams();
  const [faqPage, setFaqPage] = useState(1);
  const [walkthroughsPage, setWalkthroughsPage] = useState(1);
  const walkthroughsCardRef = useRef(null);
  const securityTourRequested = searchParams.get("tour") === "security";
  const securityTourCompleted = (authUser?.completed_tour_ids ?? []).includes(MY_SECURITY_WALKTHROUGH_TOUR_ID);

  useGuidedTour(
    MY_SECURITY_WALKTHROUGH_TOUR_ID,
    buildSecurityWalkthroughSteps({
      navigate,
      hasTransactionPin: authUser?.has_transaction_pin,
      needsTransactionPin: userNeedsTransactionPin(authUser),
    }),
    { enabled: !!authUser && securityTourRequested },
  );

  // Once the security walkthrough completes, drop the ?tour=security query
  // so the URL a user reads, bookmarks, or shares does not still say "run
  // the tour". Only fires when the tour was actually requested via the FAQ
  // replay flow, and the completion id has landed on the user record.
  useEffect(() => {
    if (!securityTourRequested) return;
    if (!securityTourCompleted) return;
    const next = new URLSearchParams(searchParams);
    next.delete("tour");
    setSearchParams(next, { replace: true });
  }, [securityTourRequested, securityTourCompleted, searchParams, setSearchParams]);

  const scrollWalkthroughsCardIntoView = () => {
    walkthroughsCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const goToWalkthroughsPage = (nextPage) => {
    setWalkthroughsPage(nextPage);
    scrollWalkthroughsCardIntoView();
  };

  const { hasSoloUser, isLoading: hasSoloUserLoading } = usePartnerHasSoloUser(authUser?.partner_id);

  const availableFaqEntries = useMemo(() => {
    const userPermissionSlugs = authUser?.all_permission_slugs ?? [];
    const userRoleSlug = authUser?.role_slug;
    const hasActiveWallet = !!authUser?.has_active_wallet;
    const hasAnyWallet = !!authUser?.has_wallet;
    const hasActivePartnerVault = !!authUser?.has_active_partner_vault;

    const needsTransactionPin = userNeedsTransactionPin(authUser);

    return FAQ_ENTRIES.filter((entry) => {
      if (entry.requiresUserWallet && !hasActiveWallet) return false;
      if (entry.hiddenWhenUserHasWallet && hasAnyWallet) return false;
      if (entry.requiresPartnerVault && !hasActivePartnerVault) return false;
      if (entry.requiresUserWalletOrPartnerVault && !hasActiveWallet && !hasActivePartnerVault) return false;
      if (entry.requiresTransactionPinCapability && !needsTransactionPin) return false;
      if (entry.requiredPermissionSlug && !userPermissionSlugs.includes(entry.requiredPermissionSlug)) return false;
      if (entry.requiredAnyPermissionSlug && !entry.requiredAnyPermissionSlug.some((slug) => userPermissionSlugs.includes(slug))) return false;
      if (entry.forbiddenAnyPermissionSlug && entry.forbiddenAnyPermissionSlug.some((slug) => userPermissionSlugs.includes(slug))) return false;
      if (entry.requiredRoleSlug && userRoleSlug !== entry.requiredRoleSlug) return false;
      if (entry.requiresSoloPartner && (hasSoloUserLoading || !hasSoloUser)) return false;
      if (entry.requiresNotSoloPartner && (hasSoloUserLoading || hasSoloUser)) return false;
      return true;
    });
  }, [authUser, hasSoloUser, hasSoloUserLoading]);

  const faqLastPage = Math.max(1, Math.ceil(availableFaqEntries.length / FAQ_PAGE_SIZE));
  const clampedFaqPage = Math.min(faqPage, faqLastPage);
  const visibleFaqEntries = availableFaqEntries.slice(
    (clampedFaqPage - 1) * FAQ_PAGE_SIZE,
    clampedFaqPage * FAQ_PAGE_SIZE,
  );

  const availableWalkthroughs = useMemo(() => {
    const userPermissionSlugs = authUser?.all_permission_slugs ?? [];
    const userRoleSlug = authUser?.role_slug;
    const hasActiveWallet = !!authUser?.has_active_wallet;
    const hasActivePartnerVault = !!authUser?.has_active_partner_vault;
    const hasPartner = !!authUser?.partner;

    return WALKTHROUGHS.filter((walkthrough) => {
      if (walkthrough.requiredRoleSlug && userRoleSlug !== walkthrough.requiredRoleSlug) return false;
      if (walkthrough.requiredPermissionSlug && !userPermissionSlugs.includes(walkthrough.requiredPermissionSlug)) return false;
      if (walkthrough.requiresUserWallet && !hasActiveWallet) return false;
      if (walkthrough.requiresPartnerVault && !hasActivePartnerVault) return false;
      if (walkthrough.requiresPartner && !hasPartner) return false;
      if (walkthrough.requiresNotSoloPartner && (hasSoloUserLoading || hasSoloUser)) return false;
      return true;
    });
  }, [authUser, hasSoloUser, hasSoloUserLoading]);

  const walkthroughsLastPage = Math.max(1, Math.ceil(availableWalkthroughs.length / WALKTHROUGHS_PAGE_SIZE));
  const clampedWalkthroughsPage = Math.min(walkthroughsPage, walkthroughsLastPage);
  const visibleWalkthroughs = availableWalkthroughs.slice(
    (clampedWalkthroughsPage - 1) * WALKTHROUGHS_PAGE_SIZE,
    clampedWalkthroughsPage * WALKTHROUGHS_PAGE_SIZE,
  );

  const handleTakeWalkthrough = (walkthrough) => {
    const tourIdsToReset = [walkthrough.tourId, ...(walkthrough.additionalTourIdsToReset ?? [])];
    const currentIds = authUser?.completed_tour_ids ?? [];
    const idsThatWereCompleted = tourIdsToReset.filter((tourId) => currentIds.includes(tourId));

    if (idsThatWereCompleted.length > 0) {
      dispatch(
        updateAuthUser({
          completed_tour_ids: currentIds.filter((completedId) => !idsThatWereCompleted.includes(completedId)),
        })
      );
      idsThatWereCompleted.forEach((tourId) => {
        clearTourCompletion(tourId).catch((persistError) => {
          // eslint-disable-next-line no-console
          console.warn("Could not clear guided tour completion on the server.", persistError);
        });
      });
    }

    navigate(walkthrough.route);
  };

  return (
    <div className="row">
      <div className="col-12">
        <div className="card">
          <div className="card-header">
            <h4 className="mb-0">Help &amp; FAQ</h4>
          </div>
          <div className="card-body">
            <p className="text-muted">
              Replay a tour or read answers to common questions. If you cannot find what you need, contact your account administrator.
            </p>
          </div>
        </div>
      </div>

      <div className="col-12">
        <div className="card" ref={walkthroughsCardRef}>
          <div className="card-header">
            <h5 className="mb-0">Guided tours</h5>
          </div>
          <div className="card-body">
            {availableWalkthroughs.length === 0 ? (
              <p className="text-muted mb-0">
                No tours are available for your account yet. Ones that apply to you will show up here as they are released.
              </p>
            ) : (
              <>
                <div className="row g-3">
                  {visibleWalkthroughs.map((walkthrough) => (
                    <div key={walkthrough.tourId} className="col-12 col-md-6">
                      <div className="border rounded p-3 h-100 d-flex flex-column">
                        <h6 className="mb-2">{walkthrough.title}</h6>
                        <p className="text-muted small flex-grow-1 mb-3">
                          {walkthrough.description}
                        </p>
                        <div>
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={() => handleTakeWalkthrough(walkthrough)}
                          >
                            Take this tour
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {walkthroughsLastPage > 1 && (
                  <div className="d-flex justify-content-center align-items-center gap-3 mt-3">
                    <button
                      type="button"
                      className="btn btn-sm btn-link p-0 text-decoration-none"
                      disabled={clampedWalkthroughsPage === 1}
                      onClick={() => goToWalkthroughsPage(Math.max(clampedWalkthroughsPage - 1, 1))}
                      aria-label="Previous page"
                    >
                      <i className="fa fa-angle-left me-1" />
                      Prev
                    </button>
                    <span className="text-muted small">
                      Page {clampedWalkthroughsPage} of {walkthroughsLastPage}
                    </span>
                    <button
                      type="button"
                      className="btn btn-sm btn-link p-0 text-decoration-none"
                      disabled={clampedWalkthroughsPage === walkthroughsLastPage}
                      onClick={() => goToWalkthroughsPage(Math.min(clampedWalkthroughsPage + 1, walkthroughsLastPage))}
                      aria-label="Next page"
                    >
                      Next
                      <i className="fa fa-angle-right ms-1" />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="col-12">
        <div className="card">
          <div className="card-header">
            <h5 className="mb-0">Frequently asked questions</h5>
          </div>
          <div className="card-body">
            <Accordion defaultActiveKey={null}>
              {visibleFaqEntries.map((entry) => (
                <Accordion.Item key={entry.id} eventKey={entry.id}>
                  <Accordion.Header>{entry.question}</Accordion.Header>
                  <Accordion.Body>{entry.answer}</Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
            {faqLastPage > 1 && (
              <div className="d-flex justify-content-center align-items-center gap-3 mt-3">
                <button
                  type="button"
                  className="btn btn-sm btn-link p-0 text-decoration-none"
                  disabled={clampedFaqPage === 1}
                  onClick={() => setFaqPage((p) => Math.max(p - 1, 1))}
                  aria-label="Previous page"
                >
                  <i className="fa fa-angle-left me-1" />
                  Prev
                </button>
                <span className="text-muted small">
                  Page {clampedFaqPage} of {faqLastPage}
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-link p-0 text-decoration-none"
                  disabled={clampedFaqPage === faqLastPage}
                  onClick={() => setFaqPage((p) => Math.min(p + 1, faqLastPage))}
                  aria-label="Next page"
                >
                  Next
                  <i className="fa fa-angle-right ms-1" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Faq;
